import { useCallback, useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { UserResponse } from "../../../app/models/responseModels/userResponse";
import { UpdateUserRequest } from "../../../app/models/requestModels/userRequest";
import { RootState, useAppDispatch } from "../../../app/features/store/store";
import { useSelector } from "react-redux";
import { PAGES } from "../../../enums/PagesEnum";
import {
  fetchFileTypeForRoles,
  getRoleList,
} from "../../../app/features/role/roleSlice";
import getAxiosError from "../../../app/api/axiosError";
import { updateUserTokenToInvalid } from "../../../app/features/login/loginSlice";
import { toast } from "react-toastify";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import {
  deleteUser,
  fetchFdpUserDoc,
  generateUserPassword,
  updateUser,
} from "../../../app/features/user/userSlice";
import PreviewIcon from "@mui/icons-material/Preview";
import SubmitModalWithEditAndDelete from "../../../components/common/modalPopup/SubmitModalWithEditAndDelete";
import CancelIcon from "@mui/icons-material/Cancel";
import { RoleFileType } from "../../../app/models/responseModels/roleResponse";
import { CustomTooltip } from "../../../components/common/tooltip/CustomToolTip";

interface EditUserModalProps {
  closeEditUserModal: CallableFunction;
  onCompleted: CallableFunction;
  userRowData: UserResponse;
  isForFdps?: boolean;
}

function UpdateUserFormModel(
  firstName: string,
  lastName: string,
  userId: string,
  email: string,
  phone: string,
  userActionOwnerType: string,
  deleted: string,
  roles: string[],
  parcelCenterIds: string[]
): UpdateUserRequest {
  return {
    firstname: firstName,
    lastname: lastName,
    email: email,
    id: userId,
    phone: phone,
    userActionOwnerType: userActionOwnerType,
    deleted: deleted,
    roles: roles,
    parcelCenterIds: parcelCenterIds,
  };
}

const EditUserModal = ({
  closeEditUserModal,
  onCompleted,
  userRowData,
  isForFdps,
}: EditUserModalProps) => {
  const dispatch = useAppDispatch();

  // ***** Get page Permissions *****
  const pagePermission = useSelector((state: RootState) =>
    state.login?.authResponse?.userSettings?.pagePermissions.find(
      (x) => x.pageName === PAGES.USER
    )
  );

  // ***** Get All assigned parcel canters *****
  const assignedParcelCenters = useSelector(
    (state: RootState) =>
      state.login?.authResponse?.userSettings?.assignedParcelCenters
  );

  const IsUpdatePermisssion = pagePermission
    ? pagePermission.isUpdatePermission
    : false;

  const IsDeletePermisssion = pagePermission
    ? pagePermission.isDeletePermission
    : false;

  const IsAdmin = useSelector((state: RootState) =>
    state.login?.authResponse?.userSettings
      ? state.login?.authResponse?.userSettings?.roles.includes(
          "Administrators"
        ) ||
        state.login?.authResponse?.userSettings?.roles.includes("FDP_ADMIN")
      : false
  );
  const {
    isLoading: isRolesLoading,
    roleList,
    roleFileTypes,
  } = useSelector((state: RootState) => state.role);

  // ***** States *****
  const [isPageLoading, setPageLoading] = useState(false);
  const [showSubmitButton, setShowSubmitButton] = useState(false);

  const [firstName, setFirstName] = useState(
    userRowData.firstName ? userRowData.firstName : ""
  );
  const [lastName, setLastName] = useState(
    userRowData.lastName ? userRowData.lastName : ""
  );
  const [phone, setPhone] = useState(
    userRowData.phone ? userRowData.phone : ""
  );
  const [deleted, setDeleted] = useState(userRowData.deleted);
  const [firstNameError, setFirstNameError] = useState(false);
  const [roleError, setRoleError] = useState(false);

  const [roleSelectedOptions, setRoleSelectedOptions] = useState<string[]>(
    userRowData.assignedRoles
      ? userRowData.assignedRoles.map((x) => x.roleName)
      : []
  );

  const [parcelCenterSelectedOptions, setParcelCenterSelectedOptions] =
    useState<string[]>(
      userRowData.assignedParcelCenters
        ? userRowData.assignedParcelCenters.map((x) => x.parcelCenterId)
        : []
    );
  const [selectedFile, setSelectedFile] = useState<RoleFileType[]>([]);

  // ***** Callbacks *****
  // Get Roles
  const getRoleData = useCallback(async () => {
    try {
      const response = await dispatch(
        getRoleList(isForFdps as boolean)
      ).unwrap();
      if (!response.isSuccess) {
        toast.error(response.message!, {
          toastId: "roleList",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "roleListException",
      });
    }
  }, [dispatch]);

  const getFileTypesForRole = useCallback(async () => {
    try {
      const response = await dispatch(
        fetchFileTypeForRoles(roleSelectedOptions)
      ).unwrap();
      if (!response.isSuccess) {
        toast.error(response.message!, {
          toastId: "fileTypeListException",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "fileTypeListException",
      });
    }
  }, [dispatch, roleSelectedOptions]);

  // ***** UseEffects *****
  useEffect(() => {
    let isFunctionRan = true;
    if (isFunctionRan) {
      getRoleData();
    }
    return () => {
      isFunctionRan = false;
    };
  }, [getRoleData]);

  useEffect(() => {
    let isFunctionRan = true;
    if (isFunctionRan) {
      getFileTypesForRole();
    }
    return () => {
      isFunctionRan = false;
    };
  }, [getFileTypesForRole]);

  useEffect(() => {
    setSelectedFile(roleFileTypes);
  }, [roleFileTypes]);

  // ***** Handlers *****
  const handleFileSelection = (reqFileType: string, reqFile: File | null) => {
    setSelectedFile((prevSelectedFiles) =>
      prevSelectedFiles.map((prevfile) =>
        prevfile.fileType === reqFileType
          ? { ...prevfile, file: reqFile, isError: false }
          : prevfile
      )
    );
  };

  const validateForm = () => {
    let isValid = true;
    const firstNameIsEmpty = firstName.trim().length === 0;
    setFirstNameError(firstNameIsEmpty);
    if (firstNameIsEmpty) isValid = false;
    const roleSelectionIsEmpty = roleSelectedOptions.length === 0;
    setRoleError(roleSelectionIsEmpty);
    if (roleSelectionIsEmpty) isValid = false;
    return isValid && areFilesValid();
  };

  const areFilesValid = (): boolean => {
    let allFilesValid = true;
    const userPreSelecedFile = userRowData.userFileDtos || [];
    const updatedFiles = selectedFile.map((file) => {
      const isFilePreSelected = userPreSelecedFile.some(
        (preFile) => preFile.fileType === file.fileType && preFile.filePath
      );
      if (file.requiresOnBoarding && !file.file && !isFilePreSelected) {
        allFilesValid = false;
        return { ...file, isError: true };
      }
      return { ...file, isError: false };
    });
    setSelectedFile(updatedFiles);
    return allFilesValid;
  };

  const handleUpdate = async () => {
    if (!validateForm()) {
      return false;
    }
    try {
      const updateUserDetails = UpdateUserFormModel(
        firstName,
        lastName,
        userRowData.id,
        userRowData.email,
        phone,
        "null",
        deleted,
        roleSelectedOptions,
        parcelCenterSelectedOptions
      );
      const formData = new FormData();
      // Append user details as a JSON string
      formData.append(
        "updateCmdUser",
        new Blob([JSON.stringify(updateUserDetails)], {
          type: "application/json",
        })
      );
      selectedFile.forEach((file) => {
        if (file.file) {
          formData.append(file.fileType, file.file);
        }
      });
      const response = await dispatch(updateUser(formData)).unwrap();
      if (response.isSuccess) {
        toast.success(response.message!, {
          toastId: "updateUserSuccess",
        });
        onCompleted();
      } else {
        toast.error(response.message!, {
          toastId: "updateUserError",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "updateUserException",
      });
    }
    return true;
  };

  const handleDelete = async () => {
    try {
      const updateUserDetails = UpdateUserFormModel(
        firstName,
        lastName,
        userRowData.id,
        userRowData.email,
        phone,
        "null",
        deleted,
        roleSelectedOptions,
        parcelCenterSelectedOptions
      );
      const response = await dispatch(deleteUser(updateUserDetails)).unwrap();
      if (response.isSuccess) {
        toast.success(response.message!, {
          toastId: "updateUserSuccess",
        });
        onCompleted();
      } else {
        toast.error(response.message!, {
          toastId: "updateUserError",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "updateUserException",
      });
    }
    return true;
  };

  const handleGeneratePassword = async () => {
    try {
      const response = await dispatch(
        generateUserPassword(userRowData.email)
      ).unwrap();
      if (response.isSuccess) {
        toast.success(response.message!, {
          toastId: "GenerateUserPasswordSuccess",
        });
        onCompleted();
      } else {
        toast.error(response.message!, {
          toastId: "GenerateUserPasswordError",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "GenerateUserPasswordException",
      });
    }
    return true;
  };

  const getUserDoc = async (fileType: string) => {
    try {
      setPageLoading(true);
      const response = await dispatch(
        fetchFdpUserDoc({ fileType: fileType, userId: userRowData.id })
      ).unwrap();
      if (response.status !== 200) {
        toast.error("Document couldn't retrived.", {
          toastId: "FpdUserDocError",
        });
      }
      const fileName = response.headers["file-name"];
      const fileContentType = response.headers["content-type"];
      if (
        fileContentType ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        fileContentType === "application/vnd.ms-excel" ||
        fileContentType === "text/csv" ||
        fileContentType === "image/png" ||
        fileContentType === "image/jpeg"
      ) {
        const blob = new Blob([response.data], { type: fileType });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else if (fileContentType === "application/pdf") {
        // For PDF files, open in a new tab
        const pdfUrl = URL.createObjectURL(response.data);
        window.open(pdfUrl);
        setTimeout(() => {
          URL.revokeObjectURL(pdfUrl);
        }, 120000); // 120,000 milliseconds = 2 minute
      } else {
        throw new Error("Unsupported file type");
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error("File isn't accessible.", {
        toastId: "FetchFdpUserDocException",
      });
    }
    setPageLoading(false);
  };

  return (
    <>
      <SubmitModalWithEditAndDelete
        title="Edit User"
        updateAsyncOperation={handleUpdate}
        closeSubmitModalWithEditAndDelete={closeEditUserModal}
        size="lg"
        isLoading={isRolesLoading || isPageLoading}
        submitButtonName="Save"
        closeModalAfterSubmit={true}
        confirmMessage="Are you sure you want to delete?"
        passwordConfirmMessage="Are you sure you want to generate new password for user?"
        isUpdatePermission={IsUpdatePermisssion && IsAdmin}
        isDeletePermission={IsDeletePermisssion}
        setShowSubmitButton={setShowSubmitButton}
        showSubmitButton={showSubmitButton}
        showDeleteButton={IsDeletePermisssion}
        showGeneratePasswordButton={false}
        deleteAsyncOperation={handleDelete}
        generatePasswordAsyncOperation={handleGeneratePassword}
      >
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Grid container columnSpacing={2}>
            <Grid item xs={12} sm={6} md={6} lg={6}>
              <TextField
                margin="normal"
                id="email"
                label="Email"
                name="email"
                size="small"
                fullWidth
                variant={"outlined"}
                value={userRowData.email}
                disabled
              />
            </Grid>
            {/* <Grid item xs={12} sm={6} md={6} lg={6}>
              <TextField
                select
                margin="normal"
                id="active"
                label="Active"
                name="active"
                size="small"
                fullWidth
                variant={"outlined"}
                value={deleted}
                onChange={(event) => setDeleted(event.target.value)}
                disabled={!showSubmitButton}
              >
                <MenuItem value="false">True</MenuItem>
                <MenuItem value="true">False</MenuItem>
              </TextField>
            </Grid> */}
            <Grid item xs={12} sm={6} md={6} lg={6}>
              <TextField
                margin="normal"
                id="firstname"
                label="First Name"
                name="firstname"
                size="small"
                fullWidth
                variant={"outlined"}
                value={firstName}
                onChange={(event) => setFirstName(event.target.value)}
                disabled={!showSubmitButton}
                error={firstNameError}
                helperText={firstNameError ? "First Name is required" : ""}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6} lg={6}>
              <TextField
                margin="normal"
                id="lastname"
                label="Last Name"
                name="lastname"
                size="small"
                fullWidth
                variant={"outlined"}
                value={lastName}
                onChange={(event) => setLastName(event.target.value)}
                disabled={!showSubmitButton}
              ></TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={6} lg={6}>
              <Autocomplete
                onChange={(event, value) =>
                  setRoleSelectedOptions(value.map((x) => x.name))
                }
                value={roleList.filter((x) =>
                  roleSelectedOptions.includes(x.name)
                )}
                loading={isRolesLoading}
                disabled={!showSubmitButton}
                multiple
                limitTags={3}
                id="roles"
                size="small"
                options={roleList}
                getOptionLabel={(option) => option.name}
                filterSelectedOptions
                renderInput={(params) => (
                  <TextField
                    {...params}
                    margin="normal"
                    id="role"
                    label="Role"
                    name="role"
                    size="small"
                    fullWidth
                    error={roleError}
                    helperText={roleError ? "Select at least one role" : ""}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6} lg={6}>
              <Autocomplete
                onChange={(event, value) =>
                  setParcelCenterSelectedOptions(
                    value.map((x) => x.parcelCenterId)
                  )
                }
                value={
                  assignedParcelCenters &&
                  assignedParcelCenters.filter((x) =>
                    parcelCenterSelectedOptions.includes(x.parcelCenterId)
                  )
                }
                disabled={!showSubmitButton}
                multiple
                limitTags={3}
                id="parcelCenter"
                size="small"
                options={assignedParcelCenters ?? []}
                getOptionLabel={(option) => option.parcelCenterName}
                filterSelectedOptions
                renderInput={(params) => (
                  <TextField
                    {...params}
                    margin="normal"
                    id="parcelCenter"
                    label="Parcel Center"
                    name="parcelCenter"
                    size="small"
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6} lg={6}>
              <TextField
                margin="normal"
                id="phone"
                label="Phone"
                name="phone"
                size="small"
                fullWidth
                variant={"outlined"}
                value={phone}
                onChange={(event) => setPhone(event.target.value)}
                disabled={!showSubmitButton}
              />
            </Grid>
            <Grid item xs={12} sm={8} md={6} lg={6}>
              {isForFdps &&
                userRowData.userFileDtos.map((file) => (
                  <Button
                    key={file.id}
                    sx={{ m: 2 }}
                    component="label"
                    variant="outlined"
                    onClick={() => getUserDoc(file.fileType)}
                    startIcon={<PreviewIcon />}
                  >
                    {file.fileType}
                  </Button>
                ))}
            </Grid>
            {/* <Grid item xs={12} sm={12} md={12} lg={12}>
              <Typography sx={{ color: "red" }}>
                Note: Choosing an <b>"Out of Area"</b> parcel center will result
                in the assignment of all the parcel centers.
              </Typography>
            </Grid> */}
          </Grid>

          <Grid container rowSpacing={2} columnSpacing={4}>
            {isForFdps && (
              <Grid item xs={12} sm={12} md={6} lg={6}>
                {selectedFile.map((file, index) => (
                  <Box key={index}>
                    <Button
                      sx={{ m: 1 }}
                      component="label"
                      variant="contained"
                      startIcon={<CloudUploadIcon />}
                      disabled={!showSubmitButton}
                    >
                      Upload {file.fileType}
                      <input
                        id={file.fileType}
                        type="file"
                        accept=".csv, .xls, .xlsx, .pdf, image/jpeg, image/png"
                        onChange={(event) => {
                          if (
                            !event.target.files ||
                            event.target.files.length === 0
                          ) {
                            return;
                          }
                          if (
                            event.target.files &&
                            event.target.files.length > 0
                          ) {
                            handleFileSelection(
                              file.fileType,
                              event.target.files[0]
                            );
                            event.target.value = "";
                          }
                        }}
                        hidden
                      />
                    </Button>
                    {file.file && (
                      <Box
                        display="flex"
                        mt={-2}
                        mb={-1}
                        ml={1}
                        alignItems="center"
                      >
                        <Typography variant="body2" color="textSecondary">
                          {file.file.name}
                        </Typography>
                        <CustomTooltip arrow title="Remove File">
                          <IconButton
                            type="button"
                            onClick={() =>
                              handleFileSelection(file.fileType, null)
                            }
                          >
                            <CancelIcon
                              sx={{
                                display: "inline",
                                cursor: "pointer",
                                color: "primary.main",
                              }}
                            />
                          </IconButton>
                        </CustomTooltip>
                      </Box>
                    )}
                    {file.isError && (
                      <Typography ml={1} mt={-1} variant="body2" color="red">
                        {file.fileType} File is required!
                      </Typography>
                    )}
                  </Box>
                ))}
              </Grid>
            )}
          </Grid>
        </Box>
      </SubmitModalWithEditAndDelete>
    </>
  );
};

export default EditUserModal;
